// @flow

import React from 'react';
import { Table } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import useBaseTableProps from '../../../../hooks/useBaseTableProps';
import useIntl from '../../../../hooks/useIntl';
import tabMessages from '../messages';
import CancelGrossRentCertificationModal from '../CancelGrossRentCertificationModal';
import EditGrossRentCertificationModal from '../EditGrossRentCertificationModal';
import GrossRentCertificationsToolbar from '../GrossRentCertificationsToolbar';
import type { GrossRentCertification } from '../types';
import { useManageGrossRentCertificationsTable } from './useManageTable';
import { getLabeledToolbarTableSx } from './constants';

type GrossRentCertificationsTableProps = {
  grossRentCertifications: GrossRentCertification[],
  handleMenuItemClick: (value: string) => void,
  isMenuDisabled: boolean,
  menuDisabledTooltip?: string,
  isSubmittingComplete: boolean,
  isSubmittingGenerateAll: boolean,
  menuOptions: Array<{
    value: string,
    text: string,
    disabled?: boolean,
    tooltip?: string,
  }>,
  onEffectiveDateChange: (value: string) => void,
  refetch: () => void,
  selectOptions: Array<{ value: string, text: string }>,
  selectedEffectiveDate: string,
  tableHeight: number,
};

const GrossRentCertificationsTable = ({
  grossRentCertifications,
  handleMenuItemClick,
  isMenuDisabled,
  menuDisabledTooltip,
  isSubmittingComplete,
  isSubmittingGenerateAll,
  menuOptions,
  onEffectiveDateChange,
  refetch,
  selectOptions,
  selectedEffectiveDate,
  tableHeight,
}: GrossRentCertificationsTableProps) => {
  const intl = useIntl();
  const baseTableProps = useBaseTableProps();
  const {
    allColumnsHidden,
    cancelModalProps,
    columnOptions,
    count,
    dateState,
    editGrossRentCertificationMutation,
    filterState,
    filterTypeState,
    filteredHeaders,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSettingsClose,
    handleSortChange,
    modalProps,
    name,
    order,
    orderBy,
    rows,
    searchState,
    selectedColumns,
    totalCount,
  } = useManageGrossRentCertificationsTable({
    grossRentCertifications,
    refetch,
  });

  return (
    <>
      <Table
        {...baseTableProps}
        allColumnsHidden={allColumnsHidden}
        columnOptions={columnOptions}
        count={count}
        dateState={dateState}
        filterState={filterState}
        filterTypeState={filterTypeState}
        handleColumnChange={handleColumnChange}
        handleDateSubmit={handleDateSubmit}
        handleFilterChange={handleFilterChange}
        handleFilterTypeChange={handleFilterTypeChange}
        handleSearchSubmit={handleSearchSubmit}
        handleSettingsClose={handleSettingsClose}
        handleSortChange={handleSortChange}
        headers={filteredHeaders}
        height={tableHeight}
        name={name}
        order={order}
        orderBy={orderBy}
        rows={rows}
        searchState={searchState}
        selectedColumns={selectedColumns}
        totalCount={totalCount}
        sx={getLabeledToolbarTableSx(name)}
        toolbarActions={
          <GrossRentCertificationsToolbar
            handleMenuItemClick={handleMenuItemClick}
            isMenuDisabled={isMenuDisabled}
            menuDisabledTooltip={menuDisabledTooltip}
            isSubmittingComplete={isSubmittingComplete}
            isSubmittingGenerateAll={isSubmittingGenerateAll}
            menuOptions={menuOptions}
            onEffectiveDateChange={onEffectiveDateChange}
            selectOptions={selectOptions}
            selectedEffectiveDate={selectedEffectiveDate}
          />
        }
        emptyStateMessage={intl.formatMessage(
          tabMessages.tableEmptyStateMessage,
        )}
      />
      <EditGrossRentCertificationModal
        {...modalProps}
        editGrossRentCertificationMutation={editGrossRentCertificationMutation}
      />
      <CancelGrossRentCertificationModal {...cancelModalProps} />
    </>
  );
};

export default GrossRentCertificationsTable;
